/** Four independent reads, field saves and consequential row Tasks. */
import { useState } from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { Bento, PageRefusal } from "@/components/ui/box";
import { useTabDrafts, type TabDraft } from "@/components/ui/box-draft";
import type { Box, Conflict, FieldSave, ListBox, Read, Result, TaskBox } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { ApiError } from "@/lib/api/client";
import { academicStructureApi as api, type Department, type Program, type Cohort, type Semester, type CreateDepartment, type CreateProgram, type CreateCohort, type CreateSemester, type PatchDepartment, type PatchProgram, type PatchCohort, type PatchSemester } from "@/lib/api/academicStructure";
import { facultyApi } from "@/lib/api/faculty";
import { checkCommit, reasonOnError } from "@/lib/api/commitSeams";
import { currentOnConflict } from "@/lib/api/conflict";
import { structureCopy as words } from "./academic-structure-copy";
type Kind = keyof typeof words.kinds;
type Field = keyof typeof words.fields;
type Row = Department | Program | Cohort | Semester;
type Values = Record<string, string>;
type Pending = {
    key: string;
    kind: Kind;
    action: "create" | "patch" | "delete";
    id: string | null;
    effects: string[];
};
type Saved = {
    state: "confirmed";
    kind: Kind;
    id: string;
    deleted: boolean;
};
const kinds: Kind[] = ["department", "program", "cohort", "semester"];
const fields: Record<Kind, Field[]> = {
    department: ["name", "description", "lead_user_id"],
    program: ["name", "name_ar", "code", "department_id", "description", "duration_label"],
    cohort: ["name", "program_id", "entry_year", "label"],
    semester: ["name", "start_date", "end_date", "grades_due_date"],
};
const statuses: Record<Kind, string[]> = {
    department: ["active", "inactive"], program: ["active", "inactive"], cohort: ["active", "archived"], semester: ["upcoming", "active", "closed"]
};
const readValues = (kind: Kind, row?: Row): Values => Object.fromEntries([...fields[kind], "status"].map(field => [field, row && field in row ? String((row as unknown as Record<string, unknown>)[field] ?? "") : ""]));
const statusWord = (status: string) => words.statuses[status as keyof typeof words.statuses] ?? status;
const label = (kind: Kind) => words.kinds[kind];
function read(query: UseQueryResult<Row[]>): Read<Row[]> {
    if (query.data !== undefined)
        return query.isError ? {
            status: "stale", last: query.data, readAt: new Date(query.dataUpdatedAt).toISOString(), sentence: words.stale, refresh: () => { void query.refetch(); }
        } : {
            status: "ready", value: query.data, readAt: new Date(query.dataUpdatedAt).toISOString()
        };
    return query.isPending ? { status: "loading" } : {
        status: "failed", sentence: words.failed, retry: () => { void query.refetch(); }
    };
}
function payload(values: Values, only?: string): Record<string, unknown> {
    return Object.fromEntries(Object.entries(values).filter(([field]) => !field.startsWith("_") && (!only || only === field)).map(([field, value]) => [field, field === "entry_year" ? Number(value) : value.trim() || null]));
}
async function create(kind: Kind, values: Values, key: string): Promise<Row> {
    const body = payload(values);
    if (kind === "department")
        return api.createDepartment(body as unknown as CreateDepartment, key);
    if (kind === "program")
        return api.createProgram(body as unknown as CreateProgram, key);
    if (kind === "cohort") {
        delete body.status;
        return api.createCohort(body as unknown as CreateCohort, key);
    }
    return api.createSemester(body as unknown as CreateSemester, key);
}
async function patch(kind: Kind, id: string, body: Record<string, unknown>, key: string): Promise<Row> {
    if (kind === "department")
        return api.patchDepartment(id, body as PatchDepartment, key);
    if (kind === "program")
        return api.patchProgram(id, body as PatchProgram, key);
    if (kind === "cohort")
        return api.patchCohort(id, body as PatchCohort, key);
    return api.patchSemester(id, body as PatchSemester, key);
}
export function AcademicStructurePage() {
    const { me } = useAuth();
    return <Structure key={`${me?.tenant_id}/${me?.user_id}`}/>;
}
function Structure() {
    const eyebrow = useWorkspaceEyebrow();
    const client = useQueryClient();
    const [params] = useSearchParams();
    const [find, setFind] = useState<Record<string, string>>({});
    const [filter, setFilter] = useState<Record<string, string>>({
        department: "active", program: "active", cohort: "active", semester: "all"
    });
    const departments = useQuery({
        queryKey: ["departments"], queryFn: api.getDepartments
    });
    const programs = useQuery({
        queryKey: ["programs"], queryFn: api.getPrograms
    });
    const cohorts = useQuery({
        queryKey: ["cohorts", "all"], queryFn: () => api.getCohorts({ status: "all" })
    });
    const semesters = useQuery({
        queryKey: ["semesters"], queryFn: api.getSemesters
    });
    const faculty = useQuery({
        queryKey: ["faculty"], queryFn: () => facultyApi.list()
    });
    const queries: Record<Kind, UseQueryResult<Row[]>> = {
        department: departments, program: programs, cohort: cohorts, semester: semesters
    };
    const recovery = useTabDrafts<Row[]>("structure-pending-records");
    const rowDraft = useTabDrafts<Values, Pending>("structure-record");
    const fieldDraft = useTabDrafts<Values, Pending>("structure-field");
    const newDraft = useTabDrafts<Values, Pending>("structure-create");
    const [results, setResults] = useState<Record<string, Result>>({});
    const [retained, setRetained] = useState<Record<string, Row>>({});
    const [deleted, setDeleted] = useState<Record<string, boolean>>({});
    const [saves, setSaves] = useState<Record<string, FieldSave>>({});
    const refresh = async () => {
        await Promise.all(["departments", "programs", "cohorts", "semesters", "courses", "course", "dashboard", "seat-workspace", "semester-season"].map(key => client.invalidateQueries({ queryKey: [key] })));
    };
    const remember = (id: string, value: Result) => { setResults(was => ({
        ...was, [id]: value
    })); return value; };
    const releaseRecovery = (kind: Kind, record: string | null) => { const kept = recovery({
        record: kind, version: null, initial: []
    }); kept.set(rows => rows.filter(row => row.id !== record)); };
    const unknown = (id: string, draft: TabDraft<Values, Pending>, sent: Pending): Extract<Result, {
        state: "unknown";
    }> => ({
        state: "unknown", cap: "Not confirmed", requestId: sent.key, sentence: words.unknown,
        check: {
            verb: words.check, note: words.checkNote, run: async () => {
                try {
                    const checked = await checkCommit<Saved>(sent.key, value => {
                        if (!value || typeof value !== "object")
                            throw new TypeError("Unreadable receipt");
                        const result = value as Saved;
                        if (result.state !== "confirmed" || result.kind !== sent.kind || !result.id || result.deleted !== (sent.action === "delete"))
                            throw new TypeError("Unrelated receipt");
                        return result;
                    });
                    if (checked.state === "not_committed") {
                        draft.setPending(null);
                        releaseRecovery(sent.kind, sent.id);
                        if (id.startsWith("field-"))
                            setSaves(was => ({
                                ...was, [id.slice(6)]: {
                                    status: "refused", sentence: words.notRecorded
                                }
                            }));
                        return remember(id, {
                            state: "refused", cap: words.notChanged, sentence: words.notRecorded, draft: "kept"
                        });
                    }
                    if (checked.state !== "confirmed" || checked.receipt.action !== `structure.${sent.kind}.${sent.action}` || JSON.stringify(checked.receipt.subject_ids) !== JSON.stringify(sent.id ? [sent.id] : []) || sent.id && checked.receipt.result.id !== sent.id)
                        return unknown(id, draft, sent);
                    if (sent.id) {
                        const kept = recovery({
                            record: sent.kind, version: null, initial: []
                        }).value.find(row => row.id === sent.id);
                        if (kept)
                            setRetained(was => ({
                                ...was, [`${sent.kind}/${sent.id}`]: kept
                            }));
                        if (sent.action === "delete")
                            setDeleted(was => ({
                                ...was, [`${sent.kind}/${sent.id}`]: true
                            }));
                    }
                    releaseRecovery(sent.kind, sent.id);
                    draft.end();
                    if (id.startsWith("field-"))
                        setSaves(was => ({
                            ...was, [id.slice(6)]: { status: "saved" }
                        }));
                    await refresh();
                    return remember(id, {
                        state: "confirmed", at: checked.receipt.recorded_at, effects: sent.effects, remains: sent.action === "create" ? [words.noPlacement] : []
                    });
                }
                catch {
                    return unknown(id, draft, sent);
                }
            }
        },
    });
    const resultFor = (id: string, draft: TabDraft<Values, Pending>) => draft.pending ? unknown(id, draft, draft.pending) : results[id] ?? { state: "none" } as Result;
    const conflict = (kind: Kind, row: Row, draft: TabDraft<Values, Pending>): Conflict | undefined => draft.conflict ? {
        sentence: words.newer, facts: [...fields[kind], "status" as Field].map(field => ({
            label: words.fields[field], theirs: readValues(kind, row)[field] || "Not set"
        })) as Conflict["facts"],
        keepMine: {
            label: words.keepMine, run: draft.keepMine
        }, loadNewer: {
            label: words.loadNewer, run: draft.loadNewer
        },
    } : undefined;
    const send = async (id: string, draft: TabDraft<Values, Pending>, sent: Pending, execute: () => Promise<unknown>): Promise<Result> => {
        if (draft.pending)
            return unknown(id, draft, draft.pending);
        if (sent.id) {
            const source = queries[sent.kind].data?.find(row => row.id === sent.id);
            if (source)
                recovery({
                    record: sent.kind, version: null, initial: []
                }).set(was => [...was.filter(row => row.id !== source.id), source]);
        }
        draft.setPending(sent);
        try {
            const saved = await execute();
            if (sent.action !== "delete" && (!saved || typeof saved !== "object" || !("id" in saved) || !saved.id))
                throw new TypeError("Record not identified");
            if (sent.id) {
                const row = queries[sent.kind].data?.find(value => value.id === sent.id);
                if (row)
                    setRetained(was => ({
                        ...was, [`${sent.kind}/${sent.id}`]: sent.action === "delete" ? row : {
                            ...row, ...(saved as object)
                        } as Row
                    }));
                if (sent.action === "delete")
                    setDeleted(was => ({
                        ...was, [`${sent.kind}/${sent.id}`]: true
                    }));
            }
            releaseRecovery(sent.kind, sent.id);
            draft.end();
            await refresh();
            return remember(id, {
                state: "confirmed", at: new Date().toISOString(), effects: sent.effects, remains: sent.action === "create" ? [words.noPlacement] : []
            });
        }
        catch (error) {
            const current = currentOnConflict<Row>(error);
            if (current) {
                client.setQueriesData<Row[]>({ queryKey: [sent.kind === "cohort" ? "cohorts" : `${sent.kind}s`] }, rows => rows?.map(row => row.id === current.id ? {
                    ...row, ...current
                } : row));
            }
            if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
                draft.setPending(null);
                releaseRecovery(sent.kind, sent.id);
                return remember(id, {
                    state: "refused", cap: words.notChanged, sentence: reasonOnError(error)?.message ?? error.message, draft: "kept"
                });
            }
            return unknown(id, draft, sent);
        }
    };
    const options = (field: Field) => field === "department_id" ? departments.data?.map(row => ({
        value: row.id, name: row.name
    })) : field === "program_id" ? programs.data?.map(row => ({
        value: row.id, name: `${row.name}${row.status === "inactive" ? " (retired)" : ""}`
    })) : field === "lead_user_id" ? faculty.data?.filter(row => !row.archived_at && row.user_id && row.account_is_active && ["admin", "instructor"].includes(row.account_role ?? "")).map(row => ({
        value: row.user_id!, name: row.display_name ?? "Unnamed faculty record"
    })) : undefined;
    const choiceRead = (field: Field) => field === "department_id" ? departments : field === "program_id" ? programs : field === "lead_user_id" ? faculty : undefined;
    const control = (field: Field, draft: TabDraft<Values, Pending>, name: string, save?: (value: string) => void, aria = {}) => {
        const allChoices = options(field), source = choiceRead(field);
        const active = field === "department_id" ? departments.data?.filter(row => row.status === "active").map(row => row.id) : field === "program_id" ? programs.data?.filter(row => row.status === "active").map(row => row.id) : undefined;
        const choices = allChoices?.filter(choice => !active || active.includes(choice.value) || choice.value === draft.value[field]);
        const value = draft.value[field] ?? "";
        const change = (next: string) => { if (!draft.pending)
            draft.set(was => ({
                ...was, [field]: next
            })); };
        const disabled = !!draft.pending || !!draft.conflict || !!source && (!source.data || source.isError);
        return source ? <><select {...aria} className="w-full min-w-0 max-w-full" aria-label={name} value={value} disabled={disabled} onChange={event => change(event.target.value)} onBlur={event => save?.(event.target.value)}>
      <option value="">Not assigned</option>{value && !choices?.some(choice => choice.value === value) && <option value={value}>Current assignment (unavailable)</option>}{choices?.map(choice => <option key={choice.value} value={choice.value}>{choice.name}</option>)}
    </select>{source.isPending ? <p>Reading choices.</p> : source.isError ? <p>Choices could not be read. <button type="button" onClick={() => { void source.refetch(); }}>Try again</button></p> : null}</> : <Input {...aria} aria-label={name} type={field.includes("date") ? "date" : field === "entry_year" ? "number" : "text"} value={value} disabled={disabled} onChange={event => change(event.target.value)} onBlur={event => save?.(event.target.value)} onKeyDown={event => { if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        } if (event.key === "Escape") {
            event.preventDefault();
            draft.loadNewer();
        } }}/>;
    };
    const boxes: Box[] = [];
    for (const kind of kinds) {
        const query = queries[kind], id = `structure-${kind}`;
        const recovering = recovery({
            record: kind, version: null, initial: []
        }).value;
        const heldRows = [...recovering, ...Object.entries(retained).filter(([key]) => key.startsWith(`${kind}/`)).map(([, row]) => row)].filter((row, index, all) => all.findIndex(other => other.id === row.id) === index);
        const rows = [...(query.data ?? []), ...heldRows.filter(row => !query.data?.some(current => current.id === row.id))];
        const visible = rows.filter(row => (!find[kind] || row.name.toLowerCase().includes(find[kind].toLowerCase())) && (filter[kind] === "all" || row.status === filter[kind] || params.get("term") === row.id || !!retained[`${kind}/${row.id}`]));
        const drafts = new Map(rows.map(row => [row.id, rowDraft({
                record: `${kind}/${row.id}`, version: row.updated_at, initial: {
                    ...readValues(kind, row), _change: ""
                }
            })]));
        const cells = new Map(rows.flatMap(row => fields[kind].map(field => [`${row.id}/${field}`, fieldDraft({
                record: `${kind}/${row.id}/${field}`, version: row.updated_at, initial: { [field]: readValues(kind, row)[field] }
            })] as const)));
        const held = (row: Row) => fields[kind].map(field => cells.get(`${row.id}/${field}`)!);
        const list: ListBox<Row[], Row> = {
            id, kind: "list", label: label(kind).plural, job: `Find and correct ${label(kind).plural.toLowerCase()}.`, owner: { system: "imam" }, read: read(query), readTime: true, fold: "never",
            state: value => ({
                class: "quiet", word: value.length ? "On record" : "No records"
            }), detail: value => value.length ? "Institutional reference records." : label(kind).empty,
            find: {
                placeholder: `Find ${label(kind).plural.toLowerCase()}`, hidden: rows.filter(row => !deleted[`${kind}/${row.id}`]).length - visible.filter(row => !deleted[`${kind}/${row.id}`]).length, onQueryChange: value => setFind(was => ({
                    ...was, [kind]: value
                })), facets: [{
                        label: `${label(kind).single} status`, value: filter[kind], options: [{
                                value: "all", label: "All statuses"
                            }, ...statuses[kind].map(status => ({
                                value: status, label: statusWord(status)
                            }))], onChange: value => setFilter(was => ({
                            ...was, [kind]: value
                        }))
                    }]
            },
            rows: () => visible, figures: value => [{
                    value: value.length, unit: label(kind).plural.toLowerCase()
                }, ...(kind === "program" ? [{
                        value: value.filter(row => row.status === "active").length, unit: "offered to applicants"
                    }] : kind === "semester" ? [{
                        value: value.filter(row => row.status !== "closed").length, unit: "open terms"
                    }] : [])],
            draft: [...drafts.values(), ...cells.values()].some(draft => draft.kept || !!draft.pending),
            table: {
                title: "Record", columns: fields[kind].map(field => ({
                    id: field, label: words.fields[field]
                })), note: "Each value saves when you leave its field. Status changes are confirmed in the row's foot."
            },
            row: {
                key: row => row.id, title: row => row.name, startsOpen: row => params.get("term") === row.id, facts: row => deleted[`${kind}/${row.id}`] ? "Deleted; confirmation kept" : statusWord(row.status), act: () => ({
                    does: "open", verb: label(kind).change
                }),
                cells: row => fields[kind].map(field => {
                    if (deleted[`${kind}/${row.id}`])
                        return {
                            id: field, read: readValues(kind, row)[field] || "Not set"
                        };
                    const draft = cells.get(`${row.id}/${field}`)!;
                    const cell = `${kind}/${row.id}/${field}`;
                    const save = async (value: string) => {
                        if (draft.pending || draft.conflict || value === readValues(kind, row)[field])
                            return;
                        const key = crypto.randomUUID(), sent: Pending = {
                            key, kind, action: "patch", id: row.id, effects: [`${words.fields[field]} was saved for ${row.name}.`]
                        };
                        setSaves(was => ({
                            ...was, [cell]: { status: "saving" }
                        }));
                        draft.setPending(sent);
                        const finish = draft.sending();
                        try {
                            const saved = await patch(kind, row.id, {
                                ...payload({ [field]: value }), expected_updated_at: draft.began
                            }, key);
                            if (saved.id !== row.id || typeof saved.updated_at !== "string")
                                throw new TypeError("Record not identified");
                            finish(saved.updated_at);
                            draft.setPending(null);
                            setSaves(was => ({
                                ...was, [cell]: { status: "saved" }
                            }));
                            await refresh();
                        }
                        catch (error) {
                            const current = currentOnConflict<Row>(error);
                            if (current)
                                client.setQueriesData<Row[]>({ queryKey: [kind === "cohort" ? "cohorts" : `${kind}s`] }, previous => previous?.map(item => item.id === current.id ? {
                                    ...item, ...current
                                } : item));
                            if (error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
                                draft.setPending(null);
                                setSaves(was => ({
                                    ...was, [cell]: {
                                        status: "refused", sentence: reasonOnError(error)?.message ?? error.message
                                    }
                                }));
                            }
                            else
                                setSaves(was => ({
                                    ...was, [cell]: {
                                        status: "unknown", check: () => { void unknown(`row-${kind}-${row.id}`, draft, sent).check.run(); }
                                    }
                                }));
                        }
                    };
                    return {
                        id: field, save: draft.pending ? {
                            status: "unknown", check: () => { void unknown(`field-${cell}`, draft, draft.pending!).check.run(); }
                        } : saves[cell] ?? { status: "idle" }, render: (_labelledBy, aria) => <>{control(field, draft, `${words.fields[field]} ${row.name}`, value => { void save(value); }, aria)}{draft.conflict && <div role="group" aria-label={`${words.fields[field]} conflict for ${row.name}`}><p>{words.newer}</p><dl><dt>{`Current ${words.fields[field].toLowerCase()}`}</dt><dd>{readValues(kind, row)[field] || "Not set"}</dd></dl><button type="button" data-act="check" onClick={() => { draft.keepMine(); setSaves(was => ({ ...was, [cell]: { status: "idle" } })); }}>{words.keepMine}</button>{" "}<button type="button" data-act="check" onClick={() => { draft.loadNewer(); setSaves(was => ({ ...was, [cell]: { status: "idle" } })); }}>{words.loadNewer}</button></div>}</>
                    };
                }),
                task: row => {
                    const draft = drafts.get(row.id)!, task = `row-${kind}-${row.id}`, part = `${task}-change`;
                    const rowConflict = conflict(kind, row, draft);
                    const selected = draft.value._change, counts = Number.isInteger(row.section_count) && Number.isInteger(row.enrollment_count);
                    const effect = selected === "delete" ? `Delete ${row.name}. ${"program_count" in row && Number.isInteger(row.program_count) ? row.program_count : "Unread"} programs are filed under it.` : `${row.name} becomes ${statusWord(selected || "the chosen status").toLowerCase()}. ${counts ? `${row.section_count} sections and ${row.enrollment_count} enrollments keep their records.` : "Affected section and enrollment counts have not been read."}`;
                    return {
                        parts: [{
                                id: part, label: label(kind).change, state: {
                                    class: "draft", word: words.notChanged
                                }, owner: { system: "imam" }, fold: "never", draft: "browser", keep: draft, conflict: rowConflict, render: () => <><label>{words.choose}<select aria-label={`${words.choose} ${row.name}`} disabled={!!draft.pending} value={selected} onChange={event => { remember(task, { state: "none" }); draft.set(was => ({
                                    ...was, _change: event.target.value
                                })); }}><option value="">{words.choose}</option>{[...statuses[kind].filter(status => status !== row.status), ...(kind === "department" ? ["delete"] : [])].map(status => <option key={status} value={status}>{statusWord(status)}</option>)}</select></label>{kind === "semester" && <TermSeasonRecord id={row.id} enabled={selected === "closed" || params.get("term") === row.id}/>}</>
                            }],
                        result: resultFor(task, draft), commit: {
                            verb: selected === "delete" ? "Delete empty department" : "Confirm status change", cap: "When confirmed", effects: () => [{
                                    id: "change", sentence: effect
                                }, {
                                    id: "boundary", sentence: !selected ? "Choose a change to read its institutional effect." : selected === "delete" ? "Only an empty department is deleted; no program, section or enrollment is moved." : selected === "active" ? kind === "semester" ? "The term is open for publication and enrollment. Existing records stay." : kind === "cohort" ? "This cohort becomes available for new placement. Existing learner records and enrollments stay." : kind === "program" ? "This program becomes available to applicants. Existing sections and enrollments stay." : "This department becomes active. Existing programs, sections and enrollments stay." : selected === "upcoming" ? "The term is upcoming. Existing sections and enrollments stay; publication still follows the term's rules." : words.boundary[kind]
                                }],
                            blockers: () => [...(!selected ? [{
                                        kind: "missing" as const, part, sentence: words.choose
                                    }] : []), ...(held(row).some(value => value.kept || !!value.pending) ? [{
                                        kind: "missing" as const, part, sentence: "Save or restore the edited fields before changing status."
                                    }] : []), ...(!counts ? [{
                                        kind: "missing" as const, part, sentence: "Read the affected records before confirming."
                                    }] : []), ...(rowConflict ? [{
                                        kind: "conflict" as const, part, sentence: words.newer
                                    }] : []), ...(selected === "delete" && "program_count" in row && row.program_count > 0 ? [{
                                        kind: "missing" as const, part, sentence: "Move this department's programs first, or retire it instead."
                                    }] : [])],
                            run: key => send(task, draft, {
                                key, kind, action: selected === "delete" ? "delete" : "patch", id: row.id, effects: [effect]
                            }, () => selected === "delete" ? api.deleteDepartment(row.id, draft.began!, key) : patch(kind, row.id, {
                                status: selected, expected_updated_at: draft.began
                            }, key)),
                        }
                    };
                },
            },
        };
        boxes.push(list as Box);
        const draft = newDraft({
            record: kind, version: null, initial: readValues(kind)
        }), part = `new-${kind}-values`, task = `new-${kind}`;
        const required = ["name", ...(kind === "cohort" ? ["entry_year", "label"] : ["status"])];
        const taskBox: TaskBox<Row[]> = {
            id: task, kind: "task", label: label(kind).create, job: `Create one ${label(kind).single}.`, owner: { system: "imam" }, read: draft.settled ? read(query) : { status: "loading" }, fold: "choice", detail: () => words.noPlacement, state: () => ({
                class: "quiet", word: "Not created"
            }), act: {
                does: "open", verb: label(kind).create
            }, draft: draft.kept,
            parts: [{
                    id: part, label: `${label(kind).single} values`, state: {
                        class: "draft", word: "Not created"
                    }, markedByBox: true, owner: { system: "imam" }, fold: "never", draft: "browser", keep: draft, render: () => <>{fields[kind].map(field => <label key={field}>{words.fields[field]}{control(field, draft, `${words.fields[field]} for new ${label(kind).single}`)}</label>)}{kind !== "cohort" && <label>{words.fields.status}<select aria-label={`Status for new ${label(kind).single}`} disabled={!!draft.pending} value={draft.value.status} onChange={event => draft.set(was => ({
                        ...was, status: event.target.value
                    }))}><option value="">Choose status</option>{statuses[kind].map(status => <option key={status} value={status}>{statusWord(status)}</option>)}</select></label>}</>
                }],
            result: resultFor(task, draft), commit: {
                verb: label(kind).create, cap: "When created", effects: () => [{
                        id: "record", sentence: `${draft.value.name || "This record"} becomes a ${label(kind).single}. ${words.noPlacement}`
                    }],
                blockers: () => [...(kind === "cohort" && (!Number.isInteger(Number(draft.value.entry_year)) || Number(draft.value.entry_year) < 2000 || Number(draft.value.entry_year) > 2099) ? [{
                            kind: "missing" as const, part, sentence: "Enter a year from 2000 to 2099."
                        }] : []), ...required.filter(field => !draft.value[field]?.trim()).map(field => ({
                        kind: "missing" as const, part, sentence: `Enter ${words.fields[field as Field].toLowerCase()}.`
                    })), ...fields[kind].filter(field => choiceRead(field) && (!choiceRead(field)!.data || choiceRead(field)!.isError)).map(field => ({
                        kind: "missing" as const, part, sentence: `Read ${words.fields[field].toLowerCase()} choices first.`
                    }))],
                run: key => send(task, draft, {
                    key, kind, action: "create", id: null, effects: [`${draft.value.name} was created as a ${label(kind).single}.`]
                }, () => create(kind, draft.value, key)),
            }
        };
        boxes.push(taskBox as Box);
    }
    const refused = Object.values(queries).some(query => query.error instanceof ApiError && [401, 403].includes(query.error.status));
    return <div className="app-page"><AcademicPageHeader eyebrow={eyebrow} title={words.title} description={words.description}/><RegistrarLocalNavigation area="courses"/>
    {refused ? <PageRefusal id="academic-structure" journey="academic-structure" refusal={{
        reason: "forbidden", sentence: "You don't have access to these records.", back: {
            label: "Courses", to: "/admin/courses"
        }
    }}/> : <Bento id="academic-structure" journey="academic-structure" boxes={boxes} defaultOpen={params.get("tab") ? [`structure-${params.get("tab")?.replace(/s$/, "")}`] : []}/>}
  </div>;
}
/** The former term sheet is the term row's read-only review, never a dialog. */
function TermSeasonRecord({ id, enabled }: {
    id: string;
    enabled: boolean;
}) {
    const query = useQuery({
        queryKey: ["semester-season", id], queryFn: () => api.getSemesterSeason(id), enabled
    });
    if (!enabled)
        return null;
    if (!query.data)
        return <p>{query.isPending ? "Reading the term review." : "The term review could not be read. This is not an all-clear."}{query.isError && <button type="button" onClick={() => { void query.refetch(); }}>Try again</button>}</p>;
    const review = query.data;
    const categories = [
        ["Published sections stay published", review.close_review.published_sections_remain_published],
        ["Scheduled sessions remain", review.close_review.scheduled_sessions],
        ["Attendance gaps", review.close_review.missing_attendance],
        ["Handed in, not yet released", review.close_review.handed_in_not_released],
        ["Returned, awaiting a new hand-in", review.close_review.returned_awaiting_resubmission],
        ["Results await proposal", review.close_review.unproposed_results],
        ["Proposals await certification", review.close_review.pending_certification],
    ] as const;
    return <section aria-label="Term review"><p>{review.term.status === "closed" ? "Historical term file" : "Term close review"}. These warnings do not block closing.</p>
    {query.isError && <p>{words.stale}</p>}
    <dl><dt>Sections</dt><dd>{review.season.section_count}</dd><dt>Credit enrollments (enrolled)</dt><dd>{review.season.active_enrollment_count}</dd><dt>Distinct credit learners (enrolled)</dt><dd>{review.season.distinct_enrolled_learner_count}</dd></dl>
    <p>Grades due: {review.term.grades_due_date ?? "Not set"}.</p>
    {categories.map(([name, rows]) => <section key={name}><h4>{name}: {rows.reduce((total, row) => total + (row.count ?? 1), 0)}</h4><ul>{rows.map((row, index) => <li key={`${row.course_id}/${row.assignment_id ?? row.session_id ?? row.enrollment_id ?? index}`}><Link data-nav="" to={row.href}>{[row.course_code, row.course_title, row.assignment_title, row.display_name, row.count === undefined ? null : `${row.count} ${row.count === 1 ? "learner" : "learners"}`].filter(Boolean).join(" · ")}</Link><span>{row.responsible_teaching_team.map(person => person.name).join(", ") || "No active teaching team"}</span></li>)}</ul></section>)}
  </section>;
}
